import React, { useState, useEffect } from "react";
import ReactTable from "react-table-v6";
import Loader from "../../../../../../settings/loader";
import commaNumber from "comma-number";
import { exportPdf } from "../../../../../../exportPdf";
import "react-table-v6/react-table.css";
import "./style.scss";

const Slider4 = (props) => {
  const [data, setData] = useState({
    result: null,
    table: [],
    brandwiseData: [],
  });
  const [load, setLoad] = useState(0);
  const [activeBrand, setActiveBrand] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [filterState, setFilterState] = useState([]);
  const tableRef = React.useRef(null);

  //filter based export
  // Define all utility functions first
  const topParser = (tableData) => {
    if (!tableData || tableData.length === 0) {
      return { max: 0, ave: 0, min: 0 };
    }

    let qtyArray = tableData.map((item) => item.qty);

    return {
      max: Math.max(...qtyArray),
      ave: qtyArray[Math.floor((qtyArray.length - 1) / 2)],
      min: Math.min(...qtyArray),
    };
  };

  const sortSales = (a, b) => {
    return parseInt(b.qty) - parseInt(a.qty);
  };

  // Handle filtered data from ReactTable
  const handleFilteredDataChange = (filtered) => {
    setFilterState(filtered);

    // If there are active filters, compute filtered data
    if (filtered && filtered.length > 0) {
      const currentTableData = getCurrentTableData();
      const filteredResults = currentTableData.filter((item) => {
        return filtered.every((filter) => {
          const value = item[filter.id];
          const filterValue = filter.value.toLowerCase();

          if (typeof value === "string") {
            return value.toLowerCase().includes(filterValue);
          } else if (typeof value === "number") {
            return String(value).toLowerCase().includes(filterValue);
          }
          return true;
        });
      });
      setFilteredData(filteredResults);
    } else {
      // No filters applied
      setFilteredData([]);
    }
  };

  const _initialized = () => {
    if (props.data && !load) {
      setData((prevData) => ({
        ...prevData,
        result: {
          ...props.filter.data,
          ...props.data,
        },
      }));
      setLoad(1);
    }
  };

  const loadData = () => {
    if (load === 1 && data.result) {
      let items = [];

      data.result.sales.data.forEach((dataItem) => {
        dataItem.items.forEach((item) => {
          const brand = props.data.brands.data.find(
            (brand) => brand.braKey === dataItem.braKey
          );
          const chain = props.data.chains.data.find(
            (chain) => chain.chaKey === dataItem.chaKey
          );

          if (!brand || !chain) return;

          // Find existing item with same refNo, name, and brandKey
          const queryItem = items.find(
            (storedItem) =>
              storedItem.refNo === item.refNo &&
              storedItem.name === item.name &&
              storedItem.brandKey === brand.braKey
          );

          let qty = item.qty || 1;
          const itemPrice = item.amount; // Original price before discount
          const discountAmount = item.discount ? item.discount.discountAmt : 0;
          const finalPrice = itemPrice - discountAmount;
          const totalAmount = finalPrice * qty;

          const discountQty =
            item.discount && item.discount.discountAmt > 0 ? qty : 0;
          const totalDiscountAmount = discountAmount * qty;

          if (!queryItem) {
            // Create new item entry
            items.push({
              ...item,
              qty: qty,
              amount: itemPrice, // Store original price
              discountQty: discountQty,
              total: totalAmount,
              totalDiscountAmount: totalDiscountAmount,
              brandKey: brand.braKey,
              brandName: brand.name,
              businessName: [
                {
                  name: `${brand.name} - ${chain.name}`,
                  amount: totalAmount,
                  qty: qty,
                  currency: chain?.currency?.toUpperCase(),
                },
              ],
              // Store price variants for items with same refNo but different prices
              priceVariants: [
                {
                  price: itemPrice,
                  discount: item.discount,
                  qty: qty,
                  total: totalAmount,
                  totalDiscountAmount: totalDiscountAmount,
                  businessName: [
                    {
                      name: `${brand.name} - ${chain.name}`,
                      amount: totalAmount,
                      qty: qty,
                      currency: chain?.currency?.toUpperCase(),
                    },
                  ],
                },
              ],
              originalDiscount: item.discount,
              aggregatedDiscount: item.discount
                ? {
                    discountAmt: item.discount.discountAmt,
                    totalDiscountAmount: totalDiscountAmount,
                  }
                : null,
            });
          } else {
            // Check if this is a different price variant
            const existingPriceVariant = queryItem.priceVariants.find(
              (variant) => variant.price === itemPrice
            );

            if (existingPriceVariant) {
              // Same price variant - aggregate
              existingPriceVariant.qty += qty;
              existingPriceVariant.total += totalAmount;
              existingPriceVariant.totalDiscountAmount += totalDiscountAmount;

              const existingBusiness = existingPriceVariant.businessName.find(
                (businessName) =>
                  businessName.name === `${brand.name} - ${chain.name}`
              );

              if (existingBusiness) {
                existingBusiness.amount += totalAmount;
                existingBusiness.qty += qty;
              } else {
                existingPriceVariant.businessName.push({
                  name: `${brand.name} - ${chain.name}`,
                  amount: totalAmount,
                  qty: qty,
                  currency: chain?.currency?.toUpperCase(),
                });
              }
            } else {
              // Different price variant - add as new variant
              queryItem.priceVariants.push({
                price: itemPrice,
                discount: item.discount,
                qty: qty,
                total: totalAmount,
                totalDiscountAmount: totalDiscountAmount,
                businessName: [
                  {
                    name: `${brand.name} - ${chain.name}`,
                    amount: totalAmount,
                    qty: qty,
                    currency: chain?.currency?.toUpperCase(),
                  },
                ],
              });
            }

            // Update main item aggregated values
            queryItem.qty += qty;
            queryItem.discountQty += discountQty;
            queryItem.total += totalAmount;
            queryItem.totalDiscountAmount += totalDiscountAmount;

            // Update main item business names
            const existingMainBusiness = queryItem.businessName.find(
              (businessName) =>
                businessName.name === `${brand.name} - ${chain.name}`
            );

            if (existingMainBusiness) {
              existingMainBusiness.amount += totalAmount;
              existingMainBusiness.qty += qty;
            } else {
              queryItem.businessName.push({
                name: `${brand.name} - ${chain.name}`,
                amount: totalAmount,
                qty: qty,
                currency: chain?.currency?.toUpperCase(),
              });
            }

            // Update aggregated discount
            if (totalDiscountAmount > 0) {
              if (!queryItem.aggregatedDiscount) {
                queryItem.aggregatedDiscount = {
                  discountAmt: 0,
                  totalDiscountAmount: 0,
                };
              }
              queryItem.aggregatedDiscount.totalDiscountAmount +=
                totalDiscountAmount;
              queryItem.aggregatedDiscount.discountAmt =
                queryItem.aggregatedDiscount.totalDiscountAmount /
                queryItem.qty;
            }
          }
        });
      });

      // const filteredItems = items.filter((item) => item);
      const filteredItems = items.filter((item) =>
        item.priceVariants
          ? item.priceVariants.some((variant) => variant.price > 0)
          : item.amount > 0
      );
      const brandwiseData = props.data.brands.data
        .map((brand) => ({
          brandName: brand.name,
          brandKey: brand.braKey,
          items: filteredItems
            .filter((item) => item.brandKey === brand.braKey)
            .sort(sortSales),
        }))
        .filter((brand) => brand.items.length > 0);

      setData((prevData) => ({
        ...prevData,
        table: filteredItems.sort(sortSales),
        brandwiseData: brandwiseData,
      }));
      setLoad(2);
    }
  };

  useEffect(() => {
    if (load === 0) {
      _initialized();
    } else if (load === 1) {
      loadData();
    }
  }, [load, props.data, props.filter?.data]);

  useEffect(() => {
    if (data.brandwiseData.length === 1 && activeBrand === 0) {
      setActiveBrand(1);
    } else if (data.brandwiseData.length === 1 && activeBrand !== 1) {
      setActiveBrand(1);
    }
  }, [data.brandwiseData.length, activeBrand]);

  // Reset filtered data when brand changes or data updates
  useEffect(() => {
    setFilteredData([]);
    setFilterState([]);
  }, [activeBrand, data.table]);

  // SINGLE EXPORT useEffect
  useEffect(() => {
    if (props.export && props.getData && data.table.length > 0) {
      if (props.exportType === "pdf") {
        exportToPdf();
      } else if (props.exportType === "excel-multisheet") {
        exportAllBrandData();
      } else {
        exportCurrentBrandData();
      }
    }
  }, [
    props.export,
    data.table,
    props.filter?.data,
    props.getData,
    activeBrand,
    data.brandwiseData,
    props.exportType,
  ]);

  const exportToPdf = () => {
    const currentData =
      filteredData.length > 0 ? filteredData : getCurrentTableData();
    const currentBrandName = getCurrentBrandName();

    // Format data for PDF export - flatten price variants
    const flattenedData = [];
    currentData.forEach((item) => {
      // Guard against missing businessName
      if (!item.businessName || !Array.isArray(item.businessName)) {
        return;
      }

      if (item.priceVariants && item.priceVariants.length > 1) {
        // Main row
        flattenedData.push([
          String(item.refNo),
          String(item.name),
          "", // Empty for main row
          String(item.businessName[0]?.currency || "QAR"),
          "", // Empty for main row
          "", // Empty for main row
          commaNumber(item.total.toFixed(2)),
          item.businessName
            .map((businessName) => {
              return `${businessName.name} | Total Qty ${
                businessName.qty
              } | ${commaNumber(businessName.amount.toFixed(2))}`;
            })
            .join(" | "),
        ]);

        // Sub-rows for each price variant
        item.priceVariants.forEach((variant, index) => {
          flattenedData.push([
            "", // Empty for sub-rows
            "", // Empty for sub-rows
            String(variant.qty),
            String(variant.businessName?.[0]?.currency || "QAR"),
            commaNumber(variant.price.toFixed(2)),
            variant.totalDiscountAmount > 0
              ? commaNumber(variant.totalDiscountAmount.toFixed(2))
              : "0",
            commaNumber(variant.total.toFixed(2)),
            variant.businessName && Array.isArray(variant.businessName)
              ? variant.businessName
                  .map((businessName) => {
                    return `${businessName.name} | Qty ${
                      businessName.qty
                    } | ${commaNumber(businessName.amount.toFixed(2))}`;
                  })
                  .join(" | ")
              : "",
          ]);
        });
      } else {
        // Single price item
        const variant = item.priceVariants?.[0] || item;
        flattenedData.push([
          String(item.refNo),
          String(item.name),
          String(item.qty),
          String(item.businessName[0]?.currency || "QAR"),
          commaNumber((variant.price || item.amount).toFixed(2)),
          item.totalDiscountAmount > 0
            ? commaNumber(item.totalDiscountAmount.toFixed(2))
            : "0",
          commaNumber(item.total.toFixed(2)),
          item.businessName
            .map((businessName) => {
              return `${businessName.name} | Qty ${
                businessName.qty
              } | ${commaNumber(businessName.amount.toFixed(2))}`;
            })
            .join(" | "),
        ]);
      }
    });

    const pdfData = {
      type: "table",
      title: `Sales by Items - ${currentBrandName}`,
      filter: getFilterData(),
      body: [
        {
          head: [
            [
              "Item No.",
              "Name",
              "Qty",
              "Currency",
              "Price",
              "Discount",
              "Sales",
              "Outlets",
            ],
          ],
          body: flattenedData,
        },
      ],
    };

    exportPdf.table(pdfData);
  };

  // Helper function for filter data
  const getFilterData = () => {
    if (data.brandwiseData.length === 1) {
      const selectedBrand = data.brandwiseData[0];
      return {
        brands: [selectedBrand.brandName],
        chains: props.filter.data.chains.map((chain) => chain.name),
        revCent: props.filter.data.revCent.map((revCent) => revCent.name),
        payType: props.filter.data.payType.map((payType) => payType.name),
        date: {
          start: props.filter.data.date.start,
          end: props.filter.data.date.end,
        },
      };
    } else if (activeBrand === 0) {
      return {
        brands: props.filter.data.brands.map((brand) => brand.name),
        chains: props.filter.data.chains.map((chain) => chain.name),
        revCent: props.filter.data.revCent.map((revCent) => revCent.name),
        payType: props.filter.data.payType.map((payType) => payType.name),
        date: {
          start: props.filter.data.date.start,
          end: props.filter.data.date.end,
        },
      };
    } else {
      const selectedBrand = data.brandwiseData[activeBrand - 1];
      return {
        brands: [selectedBrand.brandName],
        chains: props.filter.data.chains.map((chain) => chain.name),
        revCent: props.filter.data.revCent.map((revCent) => revCent.name),
        payType: props.filter.data.payType.map((payType) => payType.name),
        date: {
          start: props.filter.data.date.start,
          end: props.filter.data.date.end,
        },
      };
    }
  };

  const exportCurrentBrandData = () => {
    // Similar flattening for Excel export
    const currentData =
      filteredData.length > 0 ? filteredData : getCurrentTableData();
    const currentBrandName = getCurrentBrandName();

    const flattenedExportData = [];
    currentData.forEach((item) => {
      // Guard against missing businessName
      if (!item.businessName || !Array.isArray(item.businessName)) {
        return;
      }

      if (item.priceVariants && item.priceVariants.length > 1) {
        // Main row for Excel
        flattenedExportData.push([
          String(item.refNo),
          String(item.name),
          "", // Empty for main row
          String(item.businessName[0]?.currency || "QAR"),
          "", // Empty for main row
          "", // Empty for main row
          parseFloat(item.total.toFixed(2)),
          String(
            item.businessName
              .map((businessName) => {
                return `${businessName.name} | Total Qty ${
                  businessName.qty
                } | ${commaNumber(businessName.amount.toFixed(2))} ${
                  businessName.currency || "QAR"
                }`;
              })
              .join("\n")
          ),
        ]);

        // Sub-rows for each price variant
        item.priceVariants.forEach((variant) => {
          flattenedExportData.push([
            "", // Empty for sub-rows
            "", // Empty for sub-rows
            parseFloat(variant.qty),
            String(variant.businessName?.[0]?.currency || "QAR"),
            parseFloat(variant.price.toFixed(2)),
            parseFloat(variant.totalDiscountAmount || 0),
            parseFloat(variant.total.toFixed(2)),
            String(
              variant.businessName && Array.isArray(variant.businessName)
                ? variant.businessName
                    .map((businessName) => {
                      return `${businessName.name} | Qty ${
                        businessName.qty
                      } | ${commaNumber(businessName.amount.toFixed(2))} ${
                        businessName.currency || "QAR"
                      }`;
                    })
                    .join("\n")
                : ""
            ),
          ]);
        });
      } else {
        // Single price item
        const variant = item.priceVariants?.[0] || item;
        flattenedExportData.push([
          String(item.refNo),
          String(item.name),
          parseFloat(item.qty),
          String(item.businessName[0]?.currency || "QAR"),
          parseFloat((variant.price || item.amount).toFixed(2)),
          parseFloat(item.totalDiscountAmount || 0),
          parseFloat(item.total.toFixed(2)),
          String(
            item.businessName
              .map((businessName) => {
                return `${businessName.name} | Qty ${
                  businessName.qty
                } | ${commaNumber(businessName.amount.toFixed(2))} ${
                  businessName.currency || "QAR"
                }`;
              })
              .join("\n")
          ),
        ]);
      }
    });

    props.getData({
      type: "table",
      isMultiSheet: false,
      sheetName: `${currentBrandName}`,
      title: `Sales by Items - ${currentBrandName}`,
      filter: getFilterData(),
      body: [
        {
          head: [
            [
              "Item No.",
              "Name",
              "Qty",
              "Currency",
              "Price",
              "Discount",
              "Sales",
              "Total Sales Per Outlet",
            ],
          ],
          columnTypes: [
            "text",
            "text",
            "number",
            "text",
            "number",
            "number",
            "number",
            "text",
          ],
          body: flattenedExportData,
        },
      ],
      excelOptions: {
        columnWidths: [
          { width: 15 },
          { width: 30 },
          { width: 10 },
          { width: 10 },
          { width: 15 },
          { width: 15 },
          { width: 15 },
          { width: 40 },
        ],
        numberFormats: {
          2: "0",
          4: "0.00",
          5: "0.00",
          6: "0.00",
        },
        wrapText: [7],
      },
    });
  };

  const exportAllBrandData = () => {
    // Similar implementation for all brands
    const sheets = data.brandwiseData.map((brand) => {
      // Filter items based on current filter state
      const brandItems =
        filteredData.length > 0
          ? filteredData.filter((item) => item.brandKey === brand.brandKey)
          : brand.items;
      const flattenedData = [];
      brandItems.forEach((item) => {
        // Guard against missing businessName
        if (!item.businessName || !Array.isArray(item.businessName)) {
          return;
        }

        if (item.priceVariants && item.priceVariants.length > 1) {
          // Main row
          flattenedData.push([
            String(item.refNo),
            String(item.name),
            "", // Empty for main row
            String(item.businessName[0]?.currency || "QAR"),
            "", // Empty for main row
            "", // Empty for main row
            parseFloat(item.total.toFixed(2)),
            String(
              item.businessName
                .map((businessName) => {
                  return `${businessName.name} | Total Qty ${
                    businessName.qty
                  } | ${commaNumber(businessName.amount.toFixed(2))} ${
                    businessName.currency || "QAR"
                  }`;
                })
                .join("\n")
            ),
          ]);

          // Sub-rows
          item.priceVariants.forEach((variant) => {
            flattenedData.push([
              "", // Empty for sub-rows
              "", // Empty for sub-rows
              parseFloat(variant.qty),
              String(variant.businessName?.[0]?.currency || "QAR"),
              parseFloat(variant.price.toFixed(2)),
              parseFloat(variant.totalDiscountAmount || 0),
              parseFloat(variant.total.toFixed(2)),
              String(
                variant.businessName && Array.isArray(variant.businessName)
                  ? variant.businessName
                      .map((businessName) => {
                        return `${businessName.name} | Qty ${
                          businessName.qty
                        } | ${commaNumber(businessName.amount.toFixed(2))} ${
                          businessName.currency || "QAR"
                        }`;
                      })
                      .join("\n")
                  : ""
              ),
            ]);
          });
        } else {
          const variant = item.priceVariants?.[0] || item;
          flattenedData.push([
            String(item.refNo),
            String(item.name),
            parseFloat(item.qty),
            String(item.businessName[0]?.currency || "QAR"),
            parseFloat((variant.price || item.amount).toFixed(2)),
            parseFloat(item.totalDiscountAmount || 0),
            parseFloat(item.total.toFixed(2)),
            String(
              item.businessName
                .map((businessName) => {
                  return `${businessName.name} | Qty ${
                    businessName.qty
                  } | ${commaNumber(businessName.amount.toFixed(2))} ${
                    businessName.currency || "QAR"
                  }`;
                })
                .join("\n")
            ),
          ]);
        }
      });

      return {
        sheetName: brand.brandName.replace(/[^\w\s]/gi, "").substring(0, 31),
        head: [
          [
            "Item No.",
            "Name",
            "Qty",
            "Currency",
            "Price",
            "Discount",
            "Sales",
            "Total Sales Per Outlet",
          ],
        ],
        columnTypes: [
          "text",
          "text",
          "number",
          "text",
          "number",
          "number",
          "number",
          "text",
        ],
        body: flattenedData,
        excelOptions: {
          columnWidths: [
            { width: 15 },
            { width: 30 },
            { width: 10 },
            { width: 10 },
            { width: 15 },
            { width: 15 },
            { width: 15 },
            { width: 40 },
          ],
          numberFormats: {
            2: "0",
            4: "0.00",
            5: "0.00",
            6: "0.00",
          },
          wrapText: [7],
        },
      };
    });

    if (data.brandwiseData.length > 1) {
      const summarySheet = {
        sheetName: "Summary",
        head: [["Brand Name", "Total Items", "Total Quantity", "Total Sales"]],
        columnTypes: ["text", "number", "number", "number"],
        body: data.brandwiseData.map((brand) => {
          const brandItems =
            filteredData.length > 0
              ? filteredData.filter((item) => item.brandKey === brand.brandKey)
              : brand.items;
          const totalQty = brandItems.reduce((sum, item) => sum + item.qty, 0);
          const totalSales = brandItems.reduce(
            (sum, item) => sum + item.total,
            0
          );
          return [
            brand.brandName,
            brandItems.length,
            totalQty,
            parseFloat(totalSales.toFixed(2)),
          ];
        }),
        excelOptions: {
          columnWidths: [
            { width: 25 },
            { width: 15 },
            { width: 15 },
            { width: 20 },
          ],
          numberFormats: {
            1: "0",
            2: "0",
            3: "0.00",
          },
        },
      };
      sheets.unshift(summarySheet);
    }

    props.getData({
      type: "excel",
      isMultiSheet: true,
      title: "Sales by Items - All Brands",
      filter: getFilterData(),
      sheets: sheets,
    });
  };

  // Updated columns to display merged rows like your screenshot
  const columns = [
    {
      Header: "Item No",
      accessor: "refNo",
      Cell: (row) => {
        const item = row.original;
        return <span>{item.refNo}</span>;
      },
    },
    {
      Header: "Name",
      accessor: "name",
      Cell: (row) => {
        const item = row.original;
        return <span>{item.name}</span>;
      },
    },
    {
      id: "currency",
      Header: "Currency",
      accessor: (data) => {
        return data.businessName[0]?.currency || "QAR";
      },
      Cell: (row) => {
        return <span>{row.value}</span>;
      },
    },
    {
      Header: "Qty",
      accessor: "qty",
      Cell: (row) => {
        const item = row.original;
        const hasMultiplePrices =
          item.priceVariants && item.priceVariants.length > 1;

        if (hasMultiplePrices) {
          return (
            <div>
              {item.priceVariants.map((variant, index) => (
                <div
                  key={index}
                  style={{
                    padding: "2px 0",
                    borderBottom:
                      index < item.priceVariants.length - 1
                        ? "1px solid #dcdcdcff"
                        : "none",
                  }}
                >
                  {variant.qty}
                </div>
              ))}
            </div>
          );
        }
        return <span>{row.value}</span>;
      },
    },

    {
      Header: "Price",
      accessor: "amount",
      Cell: (row) => {
        const item = row.original;
        const hasMultiplePrices =
          item.priceVariants && item.priceVariants.length > 1;

        if (hasMultiplePrices) {
          return (
            <div>
              {item.priceVariants.map((variant, index) => (
                <div
                  key={index}
                  style={{
                    padding: "2px 0",
                    borderBottom:
                      index < item.priceVariants.length - 1
                        ? "1px solid #dcdcdcff"
                        : "none",
                  }}
                >
                  {commaNumber(variant.price.toFixed(2))}
                </div>
              ))}
            </div>
          );
        }
        return <span>{commaNumber(row.value.toFixed(2))}</span>;
      },
    },
    {
      id: "discount",
      Header: "Discount",
      accessor: (data) => {
        return data.totalDiscountAmount || 0;
      },
      Cell: (row) => {
        const item = row.original;
        const hasMultiplePrices =
          item.priceVariants && item.priceVariants.length > 1;

        if (hasMultiplePrices) {
          return (
            <div>
              {item.priceVariants.map((variant, index) => (
                <div
                  key={index}
                  style={{
                    padding: "2px 0",
                    borderBottom:
                      index < item.priceVariants.length - 1
                        ? "1px solid #dcdcdcff"
                        : "none",
                  }}
                >
                  {variant.totalDiscountAmount > 0
                    ? commaNumber(variant.totalDiscountAmount.toFixed(2))
                    : "-"}
                </div>
              ))}
            </div>
          );
        }

        const value = row.value;
        return (
          <span>{value > 0 ? `${commaNumber(value.toFixed(2))}` : "-"}</span>
        );
      },
    },
    {
      Header: "Sales",
      accessor: "total",
      Cell: (row) => {
        const item = row.original;
        const hasMultiplePrices =
          item.priceVariants && item.priceVariants.length > 1;

        if (hasMultiplePrices) {
          return (
            <div>
              {item.priceVariants.map((variant, index) => (
                <div
                  key={index}
                  style={{
                    padding: "2px 0",
                    borderBottom:
                      index < item.priceVariants.length - 1
                        ? "1px solid #dcdcdcff"
                        : "none",
                  }}
                >
                  {commaNumber(variant.total.toFixed(2))}
                </div>
              ))}
            </div>
          );
        }
        return <span>{commaNumber(row.value.toFixed(2))}</span>;
      },
    },
    {
      id: "sales",
      Header: "Total Sales Per Outlet",
      accessor: (data) => {
        return parseFloat(
          data.businessName.reduce(
            (total, businessName) => total + businessName.amount,
            0
          )
        );
      },
      Cell: (row) => {
        const item = row.original;
        return (
          <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            {item.businessName.map((businessName, index) => (
              <div key={index}>
                {`${businessName.name} | Qty ${
                  businessName.qty
                } | ${commaNumber(businessName.amount.toFixed(2))} ${
                  businessName.currency || "QAR"
                }`}
              </div>
            ))}
          </div>
        );
      },
    },
  ];

  const renderBrandTabs = () => {
    const showAllBrandsTab = data.brandwiseData.length > 1;
    return (
      <div className="brand-tabs" style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {showAllBrandsTab && (
            <button
              className={`tab-button ${activeBrand === 0 ? "active" : ""}`}
              onClick={() => setActiveBrand(0)}
              style={{
                padding: "8px 16px",
                border: "1px solid #ddd",
                backgroundColor: activeBrand === 0 ? "#f68923ff" : "#f8f9fa",
                color: activeBrand === 0 ? "white" : "#333",
                cursor: "pointer",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            >
              All Brands
            </button>
          )}
          {data.brandwiseData.map((brand, index) => (
            <button
              key={brand.brandKey}
              className={`tab-button ${
                activeBrand === index + 1 ? "active" : ""
              }`}
              onClick={() => setActiveBrand(index + 1)}
              style={{
                padding: "8px 16px",
                border: "1px solid #ddd",
                backgroundColor:
                  activeBrand === index + 1 ? "#f68923ff" : "#f8f9fa",
                color: activeBrand === index + 1 ? "white" : "#333",
                cursor: "pointer",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            >
              {brand.brandName} ({brand.items.length})
            </button>
          ))}
        </div>
      </div>
    );
  };

  const getCurrentTableData = () => {
    if (data.brandwiseData.length === 1) {
      return data.brandwiseData[0].items;
    }
    if (activeBrand === 0) {
      return data.table;
    } else {
      return data.brandwiseData[activeBrand - 1]?.items || [];
    }
  };

  const getCurrentBrandName = () => {
    if (data.brandwiseData.length === 1) {
      return data.brandwiseData[0].brandName;
    }
    if (activeBrand === 0) {
      return "All Brands";
    } else {
      return data.brandwiseData[activeBrand - 1]?.brandName || "";
    }
  };

  return (
    <div>
      {load < 2 ? (
        <Loader />
      ) : (
        <div>
          {renderBrandTabs()}

          <div
            style={{
              marginBottom: "16px",
              fontWeight: "bold",
              fontSize: "18px",
            }}
          >
            {getCurrentBrandName()} - Items Report (
            {getCurrentTableData().length} items
            {filteredData.length > 0
              ? ` - ${filteredData.length} filtered`
              : ""}
            )
          </div>

          <div
            style={{ marginBottom: "16px", fontSize: "14px", color: "#666" }}
          >
            {filteredData.length > 0 && (
              <em>
                Showing {filteredData.length} of {getCurrentTableData().length}{" "}
                items (filters applied)
              </em>
            )}
          </div>

          <ReactTable
            ref={tableRef}
            className="-striped -highlight"
            data={getCurrentTableData()}
            columns={columns}
            sortable
            filterable
            defaultPageSize={10}
            getTdProps={() => ({
              style: { textAlign: "center" },
            })}
            onFilteredChange={handleFilteredDataChange}
            noDataText={`No items found for ${getCurrentBrandName()}`}
          />
        </div>
      )}
    </div>
  );
};

export default Slider4;
